import React, { useState, useEffect } from 'react';
import {
  IonButton,
  IonIcon,
  IonSkeletonText,
} from '@ionic/react';
import ProCard from './ProCard';
import ProMetaRow from './ProMetaRow';
import {
  newspaperOutline,
  alertCircleOutline,
  chevronDownOutline,
  chevronUpOutline,
  openOutline,
} from 'ionicons/icons';
import { useQuery } from '@tanstack/react-query';
import api from '../../lib/api';

interface RSSFeedItem {
  id: string;
  title: string;
  description: string;
  link: string;
  publishedDate: string;
  author?: string;
  feedName: string;
  timeAgo: string;
}

interface RSSFeedData {
  items: RSSFeedItem[];
  sources: Array<{
    id: string;
    name: string;
    category: string;
    lastFetched?: string;
    hasError: boolean;
  }>;
  totalUnread: number;
  lastUpdated: string;
  error?: string;
}

interface ProNewsCardProps {
  queryKey?: string;
  refetchRef?: React.MutableRefObject<(() => Promise<any>) | null>;
  maxItems?: number;
  timeRange?: string;
}

const formatTimeAgo = (dateStr: string): string => {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const openLink = (url: string) => {
  if (!url) return;
  window.open(url, '_blank', 'noopener,noreferrer');
};

/**
 * Pro-namespaced news widget.
 *
 * Fork of `components/mobile/NewsCard` that swaps the raw `<a>` list inside
 * the card for `IonList` + `IonItem` rows. The shared `NewsCard` still uses
 * anchor tags so its appearance in the citizen app is unchanged.
 *
 * Differences vs the shared version:
 * - Items render as `IonItem` with native press state and chevron detail
 *   instead of raw anchor tags.
 * - External link opens through `window.open` (Capacitor WKWebView routes
 *   this to the system browser on iOS/Android).
 */
const ProNewsCard: React.FC<ProNewsCardProps> = ({
  queryKey = 'pro-rss-news',
  refetchRef,
  maxItems = 8,
  timeRange = '7d',
}) => {
  const [showAll, setShowAll] = useState(false);

  const { data, isLoading, isError, refetch } = useQuery<RSSFeedData>(
    [queryKey],
    async () => {
      const response = await api.get(
        `/dashboard/widgets/rss_news_feed/data?timeRange=${timeRange}&refreshCache=false`
      );
      return response.data?.data?.data || response.data?.data || response.data;
    },
    { staleTime: 150000, refetchInterval: 300000 },
  );

  useEffect(() => {
    if (refetchRef) {
      refetchRef.current = refetch;
    }
  }, [refetchRef, refetch]);

  const items = data?.items || [];
  const displayItems = showAll ? items : items.slice(0, maxItems);
  const hasMore = items.length > maxItems;
  const activeSources = data?.sources?.filter((s) => !s.hasError) || [];

  return (
    <ProCard clipContent padded contentClassName="px-0 pb-0">
        {isLoading ? (
          <div className="px-4 space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="space-y-1">
                <IonSkeletonText animated style={{ width: '90%', height: '16px' }} />
                <IonSkeletonText animated style={{ width: '75%', height: '14px' }} />
                <IonSkeletonText animated style={{ width: '50%', height: '12px' }} />
              </div>
            ))}
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-8 text-center px-4">
            <IonIcon icon={alertCircleOutline} className="text-3xl text-error-500 mb-2" />
            <p className="text-sm text-error-600 dark:text-error-400 mb-2">
              Unable to load news feed.
            </p>
            <IonButton fill="clear" size="small" onClick={() => refetch()}>
              Try again
            </IonButton>
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center px-4">
            <IonIcon
              icon={newspaperOutline}
              className="text-3xl text-text-tertiary dark:text-text-dark-tertiary mb-2 opacity-50"
            />
            <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
              No recent news
            </p>
            <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1">
              RSS feed may be loading
            </p>
          </div>
        ) : (
          <>
            <div className="pro-news-rows divide-y divide-border-light dark:divide-border-dark">
              {displayItems.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => openLink(item.link)}
                  className="w-full text-left px-4 py-3 transition-colors active:bg-background-tertiary dark:active:bg-background-dark-tertiary"
                >
                  <h3 className="text-sm font-medium text-text-primary dark:text-text-dark-primary leading-snug line-clamp-2">
                    {item.title}
                  </h3>
                  {item.description && (
                    <p className="text-xs text-text-secondary dark:text-text-dark-secondary line-clamp-2 mt-1">
                      {item.description}
                    </p>
                  )}
                  <ProMetaRow
                    segments={[item.feedName]}
                    trailing={
                      <span className="inline-flex items-center gap-1">
                        {item.timeAgo || formatTimeAgo(item.publishedDate)}
                        <IonIcon icon={openOutline} className="text-[10px]" color="medium" />
                      </span>
                    }
                    className="mt-1.5"
                  />
                </button>
              ))}
            </div>

            {hasMore && (
              <div className="px-2 pt-1">
                <IonButton
                  fill="clear"
                  size="small"
                  onClick={() => setShowAll(!showAll)}
                  expand="block"
                >
                  <IonIcon
                    icon={showAll ? chevronUpOutline : chevronDownOutline}
                    slot="start"
                  />
                  {showAll ? 'Show less' : `Show all ${items.length} articles`}
                </IonButton>
              </div>
            )}

            {activeSources.length > 0 && (
              <div className="mt-1 px-4 py-2 border-t border-border-light dark:border-border-dark">
                <p className="text-[11px] text-text-tertiary dark:text-text-dark-tertiary truncate">
                  Sources: {activeSources.map((s) => s.name).join(', ')}
                </p>
              </div>
            )}
          </>
        )}
    </ProCard>
  );
};

export default ProNewsCard;
